import React, { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from "lucide-react";
import { PageLightbox } from "./PageLightbox";
import { Accent, Container, SectionHeader } from "./ui";
import { samplePages } from "../data/content";
export function SamplesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    function onScroll() {
      if (!track) return;
      const slides = Array.from(track.children) as HTMLElement[];
      const base = slides[0]?.offsetLeft ?? 0;
      const left = track.scrollLeft;
      let closest = 0;
      slides.forEach((s, i) => {
        if (Math.abs(s.offsetLeft - base - left) < Math.abs(slides[closest].offsetLeft - base - left)) {
          closest = i;
        }
      });
      setActive(closest);
    }
    track.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);
  function goTo(index: number) {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slides = Array.from(track.children) as HTMLElement[];
    track.scrollTo({
      left: slides[clamped].offsetLeft - slides[0].offsetLeft,
      behavior: "smooth"
    });
  }
  const arrow = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-petrol/40 bg-white text-petrol transition-colors duration-150 ease-out hover:bg-petrol hover:text-white disabled:pointer-events-none disabled:opacity-30";
  return <section className="bg-white py-12 md:py-20" aria-label="Sample pages" data-id="element-158" data-mp="2b6s0vfoxhium">
      <Container data-id="element-159" data-mp="edzy4vlrw4pn">
        <SectionHeader eyebrow="Look inside" title={<>
              See how your next session <Accent data-id="element-161" data-mp="9ewrj6q59qfk">comes together</Accent>
            </>} lead="Preview the activity instructions and printable materials before you buy. Tap a page to view it larger." data-id="element-160" data-mp="ecf6bdquezvh" />

        <div data-id="element-162" data-mp="xb9ufpqfqr8w">
          <div className="min-w-0" aria-roledescription="carousel" data-id="element-163" data-mp="xbtf3e7gztqm">
            <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-6 pt-1" data-id="element-164" data-mp="jjll1u7kmgvm">
              {samplePages.map((p, i) => <figure key={p.src} aria-label={`${i + 1} of ${samplePages.length}`} className="w-[78%] shrink-0 snap-start sm:w-[calc((100%-16px)/2)] lg:w-[calc((100%-48px)/4)]" data-id="element-165" data-mp="1c7he8oxxlqnn" data-mpi={i}>
                  <button type="button" onClick={() => setZoomed(i)} className="group relative block w-full overflow-hidden rounded-xl border border-line bg-cream shadow-[0_12px_30px_rgba(22,78,85,0.08)]" aria-label={`View larger: ${p.label}`} data-id="element-166" data-mp="177mrmxl6vn8e">
                    <img src={p.src} alt={p.label} loading="lazy" className="aspect-[9/16] w-full object-cover object-top" data-id="element-167" data-mp="3kd1i51t923p1" />
                    <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-petrol shadow transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-168" data-mp="3fhuiq670jl5x">
                      <ZoomInIcon className="h-4 w-4" aria-hidden="true" data-id="element-169" data-mp="30qc52f1mm2pq" />
                    </span>
                  </button>
                  <figcaption className="px-1 pt-3 text-sm font-semibold text-ink" data-id="element-170" data-mp="2wq1q4cvuw1yq">{p.label}</figcaption>
                </figure>)}
            </div>

            <div className="mt-2 flex items-center justify-center gap-5" data-id="element-171" data-mp="35pot3348hftw">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous sample" className={arrow} data-id="element-172" data-mp="35nlrdltpxy9c">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-173" data-mp="2d1kmmfdu8yq5" />
              </button>
              <div className="flex gap-2" data-id="element-174" data-mp="2qo9txu5cesd0">
                {samplePages.map((p, i) => <button key={p.src} type="button" onClick={() => goTo(i)} aria-label={`Go to sample ${i + 1}`} aria-current={i === active} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? "w-7 bg-petrol" : "w-2.5 bg-petrol/25 hover:bg-petrol/45"}`} data-id="element-175" data-mp="2vml302ntcyu6" data-mpi={i} />)}
              </div>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === samplePages.length - 1} aria-label="Next sample" className={arrow} data-id="element-176" data-mp="3jdgtmwgj9nzu">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-177" data-mp="1p0qu9z7ebml2" />
              </button>
            </div>
          </div>
        </div>
      </Container>

      <PageLightbox pages={samplePages} index={zoomed} onClose={() => setZoomed(null)} onChange={setZoomed} data-id="element-178" data-mp="b3xp4nubedf3" />
    </section>;
}